<script setup lang="ts">
import type {
  FilterChangePayload,
  FilterItem,
  PageFilter,
} from '@rialtic/types'
import type { Ref } from 'vue'
import { computed, ref } from '#imports'
import { useFiltersState } from '../../composables/filters'
import { useUrlParamsState } from '../../composables/urlParamsState'
import Button from '../Button.vue'
import FloatingUi from '../FloatingUi.vue'
import ListItemCheckbox from '../list/ItemCheckbox.vue'
import FilterChip from './Chip.vue'

const props = defineProps<{
  filter: PageFilter
}>()

const { setUrlParams, getUrlParams, deleteUrlParam } = useUrlParamsState()
const { processableFilters } = useFiltersState()

const emit = defineEmits([
  'filter-all',
  'filter-change',
  'filter-clear',
  'error',
])

const inputText = ref('')

const hasActiveFilterParams = computed(
  () => processableFilters.value?.[props.filter.name],
)
const activeFilterList = computed(() => {
  return hasActiveFilterParams.value
    ? (processableFilters.value[props.filter.name] as string[]).map((item) => ({
        label: item,
        value: item,
        active: true,
      }))
    : []
})
const activator = ref<Element | null>(null) as Ref<Element>
const open = ref(false)

const activeFilterLabels = computed(() =>
  hasActiveFilterParams.value
    ? (processableFilters.value[props.filter.name] as string[]).map(
        (item) => item,
      )
    : [],
)

const setOpen = (isOpen = false) => {
  open.value = isOpen
}

const removeUrlParamFilter = (paramName: string, paramValue: string) => {
  const params = getUrlParams(paramName)
  const revisedParams = params.filter((param) => param !== paramValue)

  if (!revisedParams.length) {
    deleteUrlParam(paramName)
  } else {
    setUrlParams(paramName, revisedParams)
  }
}

const filterChange = (
  item: FilterItem & { active?: boolean },
  name: string,
) => {
  const payload: FilterChangePayload = { item, name }
  if (item.active) {
    removeUrlParamFilter(name, item.value)
  } else {
    setUrlParams(name, [...getUrlParams(name), item.value])
  }

  emit('filter-change', payload)
}
const getFilterItem = (
  value: string,
  isActive?: boolean,
): FilterItem & { active?: boolean } => {
  return {
    label: value,
    value,
    active: Boolean(isActive),
  }
}
const updateFilterList = (rawValue: string) => {
  const inputValue = rawValue.trim()

  if (!inputValue) {
    inputText.value = ''
    return
  }
  const activeFilter = activeFilterList.value?.find(
    (filter) => filter.label === inputValue,
  )

  if (activeFilter) {
    filterChange(getFilterItem(inputValue, true), props.filter.name)
  } else if (inputValue.includes(',')) {
    const inputList = inputValue
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
    for (const value of inputList) {
      filterChange(getFilterItem(value), props.filter.name)
    }
  } else {
    filterChange(getFilterItem(inputValue), props.filter.name)
  }

  inputText.value = ''
}

const clearAllFilters = (paramName: string) => {
  emit('filter-clear', paramName)

  deleteUrlParam(paramName)
}
</script>

<template>
  <div class="relative inline-block">
    <div ref="activator">
      <slot v-bind="{ open, setOpen }" name="activator">
        <FilterChip
          v-model="activeFilterLabels"
          class="h-full"
          :label="filter.label"
          button-border-radius="rounded-lg"
          @click="setOpen(true)"
        />
      </slot>
    </div>
    <FloatingUi
      v-if="open"
      class="bg-surface shadow-lg"
      :anchor="activator"
      placement="bottom-start"
      @click-outside="open = false"
    >
      <template #default="{ maxDimensions }">
        <section class="min-w-54 flex flex-col" :style="maxDimensions">
          <header class="flex items-center p-3">
            <div class="flex w-full justify-between space-x-4">
              <h3 class="h3 whitespace-nowrap text-neutral-900">
                {{ filter.label }}
              </h3>
              <button
                class="p-1"
                aria-label="Close"
                type="button"
                @click="open = false"
              >
                <div class="i-ph-x-bold" />
              </button>
            </div>
          </header>
          <div class="filter-item-container px-3">
            <div class="mb-4 flex gap-2">
              <input
                v-model="inputText"
                class="border-1 rounded-lg px-3 py-2 text-xs"
                placeholder="ex: ID1, ID2, ID3..."
                @keydown.enter="updateFilterList(inputText)"
              />
              <Button
                size="xs"
                type="button"
                @click="updateFilterList(inputText)"
              >
                Add
              </Button>
            </div>
            <ul v-if="activeFilterList.length" class="overflow-y-auto border-t">
              <li
                v-for="filterItem in activeFilterList"
                :key="filterItem.label"
              >
                <ListItemCheckbox
                  :checked="
                    !!activeFilterList.find(
                      (item) => item.label === filterItem.label,
                    )
                  "
                  @click="updateFilterList(filterItem.value)"
                >
                  {{ filterItem.label }}
                </ListItemCheckbox>
              </li>
            </ul>
          </div>
          <footer
            class="mx-3 flex justify-end border-t border-neutral-200 py-3"
          >
            <Button
              class="px-2 text-sm"
              variant="text"
              @click="clearAllFilters(filter.name)"
            >
              Clear
            </Button>
          </footer>
        </section>
      </template>
    </FloatingUi>
  </div>
</template>
